<template>
  <div class="block md:flex space-y-6 mx-8 my-6 gap-4 justify-between">
    <div class="w-full  justify-between bg-white px-5 rounded-[12px] py-4 space-y-5 border border-grayLight">

      <div class="hr-container space-x-5">

        <div class="flex justify-between">
          <p v-if="isAdmin" class="font-bold text-bodyText text-[14px]">Time Efficiency Trend</p>

          <p v-else-if="isTalent || isManager" class="font-bold text-bodyText text-[14px]">Recent Activity</p>
        </div>
      </div>

      <div class="gap-4">

        <template v-if="loading">
          <SkeletonLoader v-for="n in 6" :key="n" width="100%" height="171px" />
        </template>
        <template v-else-if="isTalent || isManager">
          <div v-for="project in limitedProjects" :key="project.project_id"
            class="w-full border border-grayLight rounded-lg py-3 px-6 space-y-2 flex flex-col justify-between items-start cursor-pointer bg-primaryBg mb-4">
            <h2 class="text-[14px] text-bodyText">{{ project.project_name }}</h2>
            <div v-if="project.project_last_update_time" class="text-[13px] text-bodyText">
              <p>
                {{ getRecentActivity(project) }}
              </p>
            </div>
            <div v-else class="text-[13px] text-gray-500">
              No recent updates.
            </div>

          </div>
        </template>
        <template v-else-if="isAdmin">
          <div
            class="w-full border border-grayLight rounded-lg py-3 px-6 space-y-2 flex flex-col justify-between items-start cursor-pointer bg-primaryBg">
            <TalentProductivityChart />
          </div>
        </template>

      </div>
    </div>

    <div v-if="isTalent || isManager"
      class="w-full justify-between bg-white px-5 rounded-[12px] py-4 space-y-5 border border-grayLight">

      <div class="hr-container space-x-5">
        <div class="flex justify-between">
          <p class="font-bold text-bodyText text-[14px]">Time Tracking</p>
        </div>
      </div>
      <p>{{ formattedTotalTimeSpent }}</p>

      <div
        class="w-full border border-grayLight rounded-lg py-3 px-6 space-y-4 flex flex-col justify-between items-start cursor-pointer bg-white-to-gray">
        <div class="w-full items-center">
          <p class="flex space-x-5 text-[12px] py-2 text-bodyText">Today's Tracked Time</p>
          <h2 class="text-[18px] text-primaryDarkBg font-semibold">{{ formattedTotalTimeSpent }}</h2>
        </div>
        <!-- <div class=" w-full space-y-2">
          <p class="flex space-x-5 text-[12px] py-2 text-bodyText">Distribution</p>
          <div
            class="w-full border border-grayLight  rounded-lg py-3 px-6 space-y-3 flex flex-col justify-between items-start cursor-pointer bg-[#F3F8EE]">
            <h2 class="text-[13px] text-grayDark">Development</h2>
            <h2 class="text-[16px] text-grayDark font-semibold">4h 43m</h2>
          </div>
          <div
            class="w-full border border-grayLight rounded-lg py-3 px-6 space-y-3 flex flex-col justify-between items-start cursor-pointer bg-[#F3EDF8]">
            <h2 class="text-[13px] text-grayDark">Development</h2>
            <h2 class="text-[16px] text-grayDark font-semibold">4h 43m</h2>
          </div>
        </div> -->
        <div class="w-full space-y-2">
          <p class="flex space-x-5 text-[12px] py-2 text-bodyText">Distribution</p>
          <div v-for="(project, index) in projectsToday" :key="index"
            class="w-full border border-grayLight rounded-lg py-3 px-6 space-y-3 flex flex-col justify-between items-start cursor-pointer"
            :class="index % 2 === 0 ? 'bg-[#F3F8EE]' : 'bg-[#F3EDF8]'">
            <h2 class="text-[13px] text-grayDark">{{ project.name }}</h2>
            <h2 class="text-[16px] text-grayDark font-semibold">{{ formattedTimeSpent(project.timeSpent) }}</h2>
          </div>
        </div>

      </div>
    </div>
  </div>

</template>

<script>
import apiClient from '@/axios';
import SkeletonLoader from '@/components/SkeletonLoader.vue';
import TalentProductivityChart from '../charts/TalentProductivityChart.vue';

export default {
  name: 'RecentActivity',
  components: {
    SkeletonLoader,
    TalentProductivityChart
  },
  data() {
    return {
      projects: [],
      tasks: [],
      filteredProjects: [],
      loading: true,
      managedProjects: [],
      activities: [],
      totalTimeSpent: 0,
      timeSpent: 0,
      projectsToday: [],
    };
  },
  computed: {
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isManager() {
      return this.$store.getters.isManager;
    },
    isTalent() {
      return this.$store.getters.isTalent;
    },
    isUser() {
      return this.$store.getters.isUser;
    },

    limitedProjects() {
      return this.filteredProjects
        .filter(project => project.project_last_update_time) // optional: skip projects with no updates
        .sort((a, b) => new Date(b.project_last_update_time) - new Date(a.project_last_update_time))
        .slice(0, 6);
    },

    totalTimeInSeconds() {
      if (!this.totalTimeSpent || isNaN(this.totalTimeSpent)) {
        return 0;
      }
      return Number(this.totalTimeSpent);
    },

    totalTimeSpentInSeconds() {
      if (!this.timeSpent || isNaN(this.timeSpent)) {
        return 0;
      }
      return Number(this.timeSpent);
    },

    formattedTotalTimeSpent() {
      if (this.totalTimeSpentInSeconds <= 0) {
        return "0h 0m 0s";
      }
      const hours = Math.floor(this.totalTimeSpentInSeconds / 3600);
      const minutes = Math.floor((this.totalTimeSpentInSeconds % 3600) / 60);
      const seconds = this.totalTimeSpentInSeconds % 60;
      return `${hours}h ${minutes}m ${seconds}s`;
    },

    formattedTotalTime() {
      if (this.totalTimeInSeconds <= 0) {
        return "0h 0m 0s";
      }
      const hours = Math.floor(this.totalTimeInSeconds / 3600);
      const minutes = Math.floor((this.totalTimeInSeconds % 3600) / 60);
      const seconds = this.totalTimeInSeconds % 60;
      return `${hours}h ${minutes}m ${seconds}s`;
    }
  },
  methods: {
    // formatDate(dateString) {
    //   if (!dateString) return 'Unknown Date';
    //   const date = new Date(dateString);
    //   return date.toLocaleString(); // Example: "MM/DD/YYYY, HH:MM:SS AM/PM"
    // },
    methods: {
      async fetchActivities(page = 1) {
        const projectId = this.$route.params.projectId;
        this.isLoading = true; // Show loading indicator

        const now = Date.now();
        const twentyFourHoursAgo = now - 24 * 60 * 60 * 1000; // 24 hours in milliseconds

        try {
          const response = await apiClient.get(`/web/projects/${projectId}/activities`, {
            params: {
              page: page,
              page_size: this.pageSize,
              talent_id: this.filters.talent || null,
              start_date: new Date(twentyFourHoursAgo).toISOString(), // Start date set to 24 hours ago
              end_date: new Date(now).toISOString(), // End date set to current time
              time_filter: this.selectedTimeFilter.value !== 'all' ? this.selectedTimeFilter.value : null,
            }
          });

          const { data, pagination } = response.data;

          // Group activities by project and sum the time spent
          const projectTimeSpent = data.reduce((acc, activity) => {
            const projectName = activity.project_name; // Assuming each activity has a `project_name`
            if (!acc[projectName]) {
              acc[projectName] = 0;
            }
            acc[projectName] += activity.duration; // Accumulate duration for each project
            return acc;
          }, {});

          // Convert project time data to an array of objects
          this.projectsToday = Object.keys(projectTimeSpent).map(projectName => ({
            name: projectName,
            timeSpent: projectTimeSpent[projectName]
          }));

          this.currentPage = page;
          this.totalPages = pagination?.total_pages || 1;

        } catch (error) {
          console.error("Error fetching activity data:", error);
          this.projectsToday = []; // Reset projects on error
        } finally {
          this.isLoading = false; // Hide loading indicator
        }
      },

      formattedTimeSpent(timeInSeconds) {
        const hours = Math.floor(timeInSeconds / 3600);
        const minutes = Math.floor((timeInSeconds % 3600) / 60);
        const seconds = timeInSeconds % 60;
        return `${hours}h ${minutes}m ${seconds}s`;
      },
      formattedTotalTimeSpent() {
        if (this.totalTimeSpent <= 0) {
          return "0h 0m 0s";
        }
        const hours = Math.floor(this.totalTimeSpent / 3600);
        const minutes = Math.floor((this.totalTimeSpent % 3600) / 60);
        const seconds = this.totalTimeSpent % 60;
        return `${hours}h ${minutes}m ${seconds}s`;
      },
    },

    formatDate(dateStr) {
      const date = new Date(dateStr);
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    getRecentActivity(project) {



      return `${project.project_name} was last updated on ${this.formatDate(project.project_last_update_time)}`;


    },
    async fetchProjects() {
      try {
        let response;

        if (this.isManager) {
          // Fetch projects managed by the manager
          response = await apiClient.get(`/web/manager/me`);
          const managerData = response.data.data; // Assuming there's only one manager in the response

          // Store the projects from the manager's data
          this.projects = managerData.projects || [];
        } else if (this.isAdmin) {
          // Fetch all projects if the user is an admin
          response = await apiClient.get('/web/projects_all');
          this.projects = response.data.data || [];
        } else if (this.isTalent) {
          // Fetch projects related to the talent
          response = await apiClient.get('/web/talent/me');
          const talentData = response.data.data;

          this.projects = talentData.projects || [];
          console.log(talentData)
          console.log('projects retrieved')
        }

        this.filterProjectsByStatus('Active'); // Filter projects by default status
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        this.loading = false;
      }
    },
    async fetchTasks(projectId) {
      try {
        const response = await apiClient.get(`/web/projects/${projectId}/tasks`);
        this.tasks = response.data.data;
      } catch (error) {
        console.error('Error fetching tasks data:', error);
      }
    },
    filterProjectsByStatus(status) {
      if (Array.isArray(this.projects)) {
        this.filteredProjects = this.projects.filter(project => project.project_status === status);
      } else {
        console.error("Projects data is not an array:", this.projects);
      }
    },
    goToProject(projectId) {
      this.$router.push({ name: 'ProjectDetails', params: { projectId } });
    },
  },
  created() {
    this.fetchProjects();
  },
};
</script>
